/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {Button} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import Tooltip from '../../tooltip/Tooltip';
import type {
  FloatingIconButtonGroupProps,
  FloatingIconButtonProps
} from '../FloatingIconButton';

type StyleProps = {
  round?: boolean;
  backgroundColor?: string;
  isGrouped?: boolean;
};

const useStyles = makeStyles(() => {
  const floatingIconButtonSize = 28;
  const groupedFloatingIconButtonWidth = 47;
  const activeBorderColor = `${dlsColor('darkBlue')}40`;
  const getDefaultBackground = ({backgroundColor}: StyleProps) => backgroundColor || dlsColor('white');
  const activeStyles = {
    background  : dlsColor('aquamarine'),
    borderColor : 'transparent',
    boxShadow   : '4px 4px 4px rgba(0, 0, 0, 0.12), inset 0px 2px 4px rgba(189, 220, 228, 0.5)',
    color       : dlsColor('darkTeal')
  };

  return ({
    root : {
      background   : getDefaultBackground,
      height       : floatingIconButtonSize,
      width        : ({isGrouped}: StyleProps) => isGrouped ? groupedFloatingIconButtonWidth : floatingIconButtonSize,
      minWidth     : ({isGrouped}: StyleProps) => isGrouped ? groupedFloatingIconButtonWidth : floatingIconButtonSize,
      padding      : '4px',
      borderRadius : ({round, isGrouped}: StyleProps) => {
        if (isGrouped) {
          return 0;
        }
        return round ? '50% !important' : '8px !important';
      },
      border     : '1px solid transparent',
      boxSizing  : 'border-box',
      boxShadow  : ({isGrouped}: StyleProps) => isGrouped ? 'none' : '4px 4px 4px rgba(0, 0, 0, 0.25)',
      gap        : '8px',
      '&:active' : {
        ...activeStyles
      },
      '&:hover' : {
        background  : getDefaultBackground,
        borderColor : dlsColor('grey5'),
        boxShadow   : dlsShadow(1.4),
        color       : dlsColor('grey1')
      },
      '&$selected' : {
        background  : dlsColor('paleBlue'),
        borderColor : activeBorderColor,
        boxShadow   : '4px 4px 4px rgba(0, 0, 0, 0.25)',
        color       : dlsColor('darkTeal')
      },
      '&$disabled' : {
        background : dlsColor('grey8'),
        boxShadow  : '4px 4px 4px rgba(0, 0, 0, 0.12)',
        color      : dlsColor('grey4')
      }
    },
    selected : {},
    shimmer  : {
      position   : 'relative',
      overflow   : 'hidden',
      '&::after' : {
        content    : '""',
        position   : 'absolute',
        top        : 0,
        left       : '-120%',
        width      : '140%',
        height     : '100%',
        background : `linear-gradient(
          120deg,
          transparent 0%,
          ${dlsColor('lightTeal')} 50%,
          transparent 100%
        )`,
        animation     : '$shimmerMove 2s infinite',
        pointerEvents : 'none'
      }
    },
    '@keyframes shimmerMove' : {
      '0%' : {
        transform : 'translateX(0)',
        opacity   : 0.80
      },
      '50%' : {
        transform : 'translateX(300%)',
        opacity   : 0.80
      },
      '50.01%, 100%' : {
        transform  : 'translateX(-120%)',
        opacity    : 0,
        background : 'transparent'
      }
    },
    disabled : {}
  });
});

export const FloatingIconButton = ({
  children,
  tooltipLabel,
  isGrouped = false,
  backgroundColor,
  className,
  isSelected = false,
  shimmer = false,
  ...props
}: FloatingIconButtonProps) => {
  const classes           = useStyles({round: props?.round, backgroundColor, isGrouped});
  const combinedClassName = clsx(classes.root, {
    [classes.shimmer]  : shimmer,
    [classes.selected] : isSelected
  }, className);
  
  const iconColor = useMemo(() => {
    let resolvedIconColor = dlsColor('grey1');
    if (props.color) {
      resolvedIconColor = props.color;
    } else if (props.disabled) {
      resolvedIconColor = dlsColor('grey4');
    } else if (isSelected) {
      resolvedIconColor = dlsColor('darkTeal');
    }
    return resolvedIconColor;
  }, [props.color, props.disabled, isSelected]);

  const button = (
    <Button variant='contained'
      classes={{
        root     : combinedClassName,
        disabled : classes.disabled
      }}
      {...props}
      color='primary'>
      {React.cloneElement(children, {
        color : iconColor
      })}
    </Button>
  );

  if (!tooltipLabel) {
    return button;
  }

  return (
    <Tooltip title={tooltipLabel}
      variant='floating'
      placement='left'
      arrow>
      <div>
        {button}
      </div>
    </Tooltip>
  );
};

type FloatingIconButtonGroupComponent = React.FC<FloatingIconButtonGroupProps> & {
  FloatingIconButton: React.ComponentType<FloatingIconButtonProps>;
};

const getFlattenedChildren = (children: JSX.Element): React.ReactNode[] => {
  return React.Children.toArray(children).flatMap((child) => {
    if (React.isValidElement(child) && child.type === React.Fragment) {
      return getFlattenedChildren(child.props.children);
    }
    return child;
  });
};

export const FloatingIconButtonGroup = (({
  children,
  isVertical,
  style = {}
}: FloatingIconButtonGroupProps) => {
  const flattenedChildren = getFlattenedChildren(children);
  const isGrouped         = !isVertical && flattenedChildren.length > 1;
  const renderedChildren  = isGrouped
    ? flattenedChildren.map((child) => React.isValidElement(child)
      ? React.cloneElement(child as React.ReactElement<FloatingIconButtonProps>, {isGrouped: true})
      : child)
    : children;

  return (
    <div style={{
      background    : isGrouped ? dlsColor('white') : undefined,
      borderRadius  : isGrouped ? 8 : undefined,
      boxShadow     : isGrouped ? '4px 4px 4px rgba(0, 0, 0, 0.25)' : 'none',
      display       : 'flex',
      filter        : isGrouped ? 'drop-shadow(0px 2px 10px rgba(0, 0, 0, 0.2))' : undefined,
      flexDirection : isVertical ? 'column' : 'row',
      height        : isGrouped ? 28 : undefined,
      overflow      : isGrouped ? 'hidden' : undefined,
      position      : isGrouped ? 'relative' : undefined,
      width         : isGrouped ? 94 : undefined,
      ...style as GenericObject
    }}>
      {isGrouped && (
        <span style={{
          background   : 'rgba(60, 60, 67, 0.3)',
          borderRadius : 8,
          bottom       : 7,
          left         : 'calc(50% - 0.5px)',
          position     : 'absolute',
          top          : 7,
          width        : 1,
          zIndex       : 1
        }} />
      )}
      {renderedChildren}
    </div>
  );
}) as FloatingIconButtonGroupComponent;

FloatingIconButtonGroup.FloatingIconButton = FloatingIconButton;

export default FloatingIconButton;
